import { useCallback, useRef, useState } from "react";
import { FEEDBACK_TIMINGS } from "../constants/feedback-timings";

export function useClipboard(timeout: number = FEEDBACK_TIMINGS.CLIPBOARD_SUCCESS_MS) {
  const [hasCopied, setHasCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const copyToClipboard = useCallback(async () => {
    // Clear any existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    try {
      if (!contentRef.current) {
        throw new Error("Content element not found");
      }

      // Get both HTML and text content
      const htmlContent = contentRef.current.innerHTML;
      const textContent = contentRef.current.textContent || "";

      if (!textContent.trim()) {
        throw new Error("No content to copy");
      }

      // Try to copy with rich text formatting
      try {
        // Create a ClipboardItem with both HTML and plain text
        const clipboardItem = new ClipboardItem({
          "text/html": new Blob([htmlContent], { type: "text/html" }),
          "text/plain": new Blob([textContent], { type: "text/plain" }),
        });

        await navigator.clipboard.write([clipboardItem]);
      }
      catch (richTextError) {
        // Fallback to plain text if rich text copying fails
        console.warn("Rich text copy failed, falling back to plain text:", richTextError);
        await navigator.clipboard.writeText(textContent);
      }

      // Set success state
      setHasCopied(true);
      setError(null);

      // Reset after timeout
      timeoutRef.current = setTimeout(() => {
        setHasCopied(false);
        timeoutRef.current = null;
      }, timeout);
    }
    catch (err) {
      setError(err instanceof Error ? err.message : "Failed to copy");
      setHasCopied(false);
      console.error("Failed to copy to clipboard:", err);
    }
  }, [timeout]);

  // Clean up timeout on unmount
  const cleanup = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  return {
    contentRef,
    hasCopied,
    error,
    copyToClipboard,
    cleanup,
  };
}
